import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  BarChart as BarChartIcon,
  ExpandMore as ExpandMoreIcon,
  OpenInNew as OpenInNewIcon,
  Refresh as RefreshIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import {
  eachDayOfInterval,
  format,
  isBefore,
  startOfDay,
  subDays,
  subMonths,
} from 'date-fns';
import * as api from '../../services/api';
import { filterMachinesByAppProduct } from '../../utils/deviceMgmtFeatures';
import DeviceFilterSelect from './DeviceFilterSelect';

export interface GalleryStatsPanelProps {
  channel: api.GalleryStatsChannel;
  userId?: string;
  accentColor?: string;
}

const LIST_PAGE_SIZE = 10;

const PRINT_SIZES: api.GalleryPrintSize[] = [
  '3x4',
  '4x3',
  '4x6',
  '6x4',
  '2x4',
  '4x2',
  '2x6',
  '6x2',
];

type TimeRangeKey = '2m' | '1m' | '15d' | '7d' | '1d';

const TIME_RANGES: { key: TimeRangeKey; label: string }[] = [
  { key: '2m', label: '2 tháng' },
  { key: '1m', label: '1 tháng' },
  { key: '15d', label: '15 ngày' },
  { key: '7d', label: '7 ngày' },
  { key: '1d', label: '1 ngày' },
];

/** Max lưu trữ thống kê */
const MAX_RETENTION_MONTHS = 2;

type ChartPoint = {
  id: string;
  label: string;
  visits: number;
  images: number;
  videos: number;
  prints: number;
};

function dayKey(d: Date): string {
  return format(d, 'yyyy-MM-dd');
}

function getRangeCutoff(range: TimeRangeKey, now = new Date()): Date {
  const today = startOfDay(now);
  switch (range) {
    case '2m':
      return startOfDay(subMonths(today, 2));
    case '1m':
      return startOfDay(subMonths(today, 1));
    case '15d':
      return startOfDay(subDays(today, 14));
    case '7d':
      return startOfDay(subDays(today, 6));
    case '1d':
    default:
      return today;
  }
}

function chipSx(selected: boolean, accent: string) {
  return {
    fontWeight: selected ? 700 : 500,
    bgcolor: selected ? alpha(accent, 0.16) : '#fff',
    color: selected ? accent : 'text.secondary',
    border: '1px solid',
    borderColor: selected ? alpha(accent, 0.5) : alpha(accent, 0.2),
    '&:hover': { bgcolor: alpha(accent, 0.1) },
  } as const;
}

const GalleryStatsPanel: React.FC<GalleryStatsPanelProps> = ({
  channel,
  userId,
  accentColor = '#e86a8a',
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<api.GalleryStatsItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selectedSizes, setSelectedSizes] = useState<api.GalleryPrintSize[]>([]);
  const [timeRange, setTimeRange] = useState<TimeRangeKey>('7d');
  const [machineCodeFilter, setMachineCodeFilter] = useState('');
  const [machines, setMachines] = useState<api.MachineInfo[]>([]);
  const [listVisibleCount, setListVisibleCount] = useState(LIST_PAGE_SIZE);
  const listSentinelRef = useRef<HTMLDivElement | null>(null);
  const loadGenRef = useRef(0);
  const fetchedFromMsRef = useRef<number | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const filteredCountRef = useRef(0);

  const isAll = selectedSizes.length === 0;

  const filteredMachines = useMemo(() => {
    const fromAccount = filterMachinesByAppProduct(machines, channel);
    const byCode = new Map<string, api.MachineInfo>();
    for (const m of fromAccount) {
      const code = String(m.machineCode || '').trim().toUpperCase();
      if (code) byCode.set(code, { ...m, machineCode: code });
    }
    for (const g of items) {
      const code = String(g.machineCode || '').trim().toUpperCase();
      if (!code || byCode.has(code)) continue;
      byCode.set(code, {
        machineCode: code,
        randomCode: '',
        machineName: '',
        isActive: true,
        appProduct: channel,
        createdAt: '',
      });
    }
    return Array.from(byCode.values()).sort((a, b) => a.machineCode.localeCompare(b.machineCode));
  }, [machines, channel, items]);

  const series = useMemo(
    () => [
      { key: 'visits' as const, label: 'Lượt truy cập', color: accentColor },
      { key: 'images' as const, label: 'Ảnh đã tải', color: '#ec4899' },
      { key: 'videos' as const, label: 'Video đã tải', color: '#a855f7' },
      { key: 'prints' as const, label: 'Số lượt in', color: '#db2777' },
    ],
    [accentColor]
  );

  const rangeCutoff = useMemo(() => getRangeCutoff(timeRange), [timeRange]);
  const maxCutoff = useMemo(() => startOfDay(subMonths(new Date(), MAX_RETENTION_MONTHS)), []);
  const effectiveCutoff = useMemo(
    () => (isBefore(rangeCutoff, maxCutoff) ? maxCutoff : rangeCutoff),
    [rangeCutoff, maxCutoff]
  );

  const load = useCallback(async (force = false) => {
    const fromMs = effectiveCutoff.getTime();
    // Already have an equal or wider window — just filter client-side
    if (!force && fetchedFromMsRef.current != null && fetchedFromMsRef.current <= fromMs) {
      setLoading(false);
      return;
    }

    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    const gen = ++loadGenRef.current;

    setLoading(true);
    setError(null);
    try {
      const from = new Date(fromMs).toISOString();
      const data = userId
        ? await api.getAdminUserGalleryStats(userId, channel, { from, signal: ac.signal })
        : await api.getUserGalleryStats(channel, { from, signal: ac.signal });
      if (gen !== loadGenRef.current) return;
      fetchedFromMsRef.current = fromMs;
      setItems(Array.isArray(data) ? data : []);
    } catch (e: any) {
      if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError' || ac.signal.aborted) return;
      if (gen !== loadGenRef.current) return;
      setError(e?.response?.data?.error || e?.message || 'Không tải được thống kê');
      setItems([]);
    } finally {
      if (gen === loadGenRef.current) setLoading(false);
    }
  }, [channel, userId, effectiveCutoff]);

  useEffect(() => {
    fetchedFromMsRef.current = null;
  }, [channel, userId]);

  useEffect(() => {
    void load();
    return () => {
      abortRef.current?.abort();
    };
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = userId
          ? await api.getAdminUserMachines(userId)
          : await api.getUserMachines();
        if (!cancelled) setMachines(res.machines || []);
      } catch (e) {
        console.error('Failed to load machines for gallery stats filter:', e);
        if (!cancelled) setMachines([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  // Reset device filter when switching product channel
  useEffect(() => {
    setMachineCodeFilter('');
  }, [channel]);

  const windowItems = useMemo(() => {
    return items.filter((g) => {
      if (!g.createdAt) return false;
      const created = new Date(g.createdAt);
      if (Number.isNaN(created.getTime())) return false;
      return !isBefore(created, effectiveCutoff);
    });
  }, [items, effectiveCutoff]);

  const filteredItems = useMemo(() => {
    let list = windowItems;
    if (machineCodeFilter) {
      const code = machineCodeFilter.toUpperCase();
      list = list.filter((g) => String(g.machineCode || '').toUpperCase() === code);
    }
    if (isAll) return list;
    const set = new Set(selectedSizes);
    return list.filter((g) => g.printSize && set.has(g.printSize as api.GalleryPrintSize));
  }, [windowItems, selectedSizes, isAll, machineCodeFilter]);

  filteredCountRef.current = filteredItems.length;

  // Reset phân trang danh sách khi đổi bộ lọc / dữ liệu
  useEffect(() => {
    setListVisibleCount(LIST_PAGE_SIZE);
  }, [timeRange, machineCodeFilter, selectedSizes, items]);

  const visibleListItems = useMemo(
    () => filteredItems.slice(0, Math.max(listVisibleCount, LIST_PAGE_SIZE)),
    [filteredItems, listVisibleCount]
  );
  const listHasMore = visibleListItems.length < filteredItems.length;

  useEffect(() => {
    if (loading) return;
    const el = listSentinelRef.current;
    if (!el || !listHasMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        setListVisibleCount((n) => {
          const total = filteredCountRef.current;
          if (total <= 0) return LIST_PAGE_SIZE;
          if (n >= total) return n;
          return Math.min(n + LIST_PAGE_SIZE, total);
        });
      },
      { rootMargin: '160px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loading, listHasMore, visibleListItems.length]);

  const totals = useMemo(() => {
    return filteredItems.reduce(
      (acc, g) => {
        acc.visits += g.visitCount || 0;
        acc.images += g.imageDownloadCount || 0;
        acc.videos += g.videoDownloadCount || 0;
        acc.prints += g.printCount || 0;
        return acc;
      },
      { visits: 0, images: 0, videos: 0, prints: 0 }
    );
  }, [filteredItems]);

  /** Luôn đủ các ngày trong khoảng (kể cả ngày = 0) */
  const chartPoints = useMemo((): ChartPoint[] => {
    const today = startOfDay(new Date());
    const days = eachDayOfInterval({ start: effectiveCutoff, end: today });

    const byDay = new Map<string, ChartPoint>();
    for (const d of days) {
      const key = dayKey(d);
      byDay.set(key, {
        id: key,
        label: format(d, 'dd/MM'),
        visits: 0,
        images: 0,
        videos: 0,
        prints: 0,
      });
    }

    for (const g of filteredItems) {
      const created = new Date(g.createdAt);
      if (Number.isNaN(created.getTime())) continue;
      const key = dayKey(created);
      const prev = byDay.get(key);
      if (!prev) continue;
      prev.visits += g.visitCount || 0;
      prev.images += g.imageDownloadCount || 0;
      prev.videos += g.videoDownloadCount || 0;
      prev.prints += g.printCount || 0;
    }

    return Array.from(byDay.values()).sort((a, b) => a.id.localeCompare(b.id));
  }, [filteredItems, effectiveCutoff]);

  const chartMax = useMemo(() => {
    let m = 1;
    for (const p of chartPoints) {
      m = Math.max(m, p.visits, p.images, p.videos, p.prints);
    }
    return m;
  }, [chartPoints]);

  const toggleSize = (size: api.GalleryPrintSize) => {
    setSelectedSizes((prev) => {
      if (prev.includes(size)) return prev.filter((s) => s !== size);
      return [...prev, size];
    });
  };

  const isInitialLoad = loading && items.length === 0 && !error;

  if (isInitialLoad) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress sx={{ color: accentColor }} />
      </Box>
    );
  }

  const emptyFilter =
    filteredItems.length === 0 && (selectedSizes.length > 0 || !!machineCodeFilter);

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <BarChartIcon sx={{ color: accentColor }} />
          <Typography variant="h6" fontWeight={700} sx={{ color: accentColor }}>
            Thống kê gallery
          </Typography>
        </Stack>
        <IconButton
          onClick={() => {
            void load(true);
          }}
          aria-label="Refresh stats"
          size="small"
          sx={{ color: accentColor }}
        >
          <RefreshIcon />
        </IconButton>
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
        Khoảng thời gian{filteredMachines.length > 0 ? ' · Thiết bị' : ''}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1,
          mb: 1.75,
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, flex: '1 1 auto', minWidth: 0 }}>
          {TIME_RANGES.map((opt) => (
            <Chip
              key={opt.key}
              label={opt.label}
              size="small"
              onClick={() => setTimeRange(opt.key)}
              sx={chipSx(timeRange === opt.key, accentColor)}
            />
          ))}
        </Box>
        {filteredMachines.length > 0 && (
          <Box sx={{ flex: { xs: '1 1 100%', sm: '0 0 auto' }, minWidth: { xs: '100%', sm: 200 }, maxWidth: { sm: 280 } }}>
            <DeviceFilterSelect
              value={machineCodeFilter}
              onChange={setMachineCodeFilter}
              machines={filteredMachines}
              accentColor={accentColor}
              fullWidth
            />
          </Box>
        )}
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
        Kích thước khung
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
        <Chip label="Tất cả" size="small" onClick={() => setSelectedSizes([])} sx={chipSx(isAll, accentColor)} />
        {PRINT_SIZES.map((size) => (
          <Chip
            key={size}
            label={size}
            size="small"
            onClick={() => toggleSize(size)}
            sx={chipSx(selectedSizes.includes(size), accentColor)}
          />
        ))}
      </Box>

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {loading && items.length > 0 && (
        <Box display="flex" justifyContent="center" py={1}>
          <CircularProgress size={22} sx={{ color: accentColor }} />
        </Box>
      )}

      {emptyFilter ? (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            textAlign: 'center',
            border: '1px solid',
            borderColor: alpha(accentColor, 0.25),
            borderRadius: 2.5,
            bgcolor: '#fff',
          }}
        >
          <Typography color="text.secondary">
            {machineCodeFilter && selectedSizes.length > 0
              ? `Không có gallery máy ${machineCodeFilter} kích thước ${selectedSizes.join(', ')} trong khoảng đã chọn.`
              : machineCodeFilter
                ? `Không có gallery của máy ${machineCodeFilter} trong khoảng thời gian đã chọn.`
                : `Không có gallery kích thước ${selectedSizes.join(', ')} trong khoảng thời gian đã chọn.`}
          </Typography>
        </Paper>
      ) : (
        <>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 1.5, sm: 2.5 },
              mb: 2.5,
              borderRadius: 2.5,
              border: '1px solid',
              borderColor: alpha(accentColor, 0.28),
              bgcolor: '#fff',
              boxShadow: `0 8px 22px ${alpha(accentColor, 0.08)}`,
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              spacing={1}
              sx={{ mb: 1.5 }}
            >
              <Typography variant="subtitle2" fontWeight={700} sx={{ color: accentColor }}>
                Biểu đồ theo thời gian
              </Typography>
              <Stack direction="row" spacing={1.5} flexWrap="wrap">
                {series.map((s) => (
                  <Stack key={s.key} direction="row" spacing={0.5} alignItems="center">
                    <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: s.color }} />
                    <Typography variant="caption" color="text.secondary">
                      {s.label}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Stack>

            <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap">
              <Typography variant="body2" fontWeight={700} sx={{ color: series[0].color }}>
                {totals.visits} lượt truy cập
              </Typography>
              <Typography variant="body2" fontWeight={700} sx={{ color: series[1].color }}>
                {totals.images} ảnh đã tải
              </Typography>
              <Typography variant="body2" fontWeight={700} sx={{ color: series[2].color }}>
                {totals.videos} video đã tải
              </Typography>
              <Typography variant="body2" fontWeight={700} sx={{ color: series[3].color }}>
                {totals.prints} số lượt in
              </Typography>
              {!isAll && (
                <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                  · {selectedSizes.join(', ')}
                </Typography>
              )}
            </Stack>

            <Box
              sx={{
                overflowX: 'auto',
                width: '100%',
                pb: 0.5,
              }}
            >
              <Box sx={{ width: '100%', minWidth: chartPoints.length * (isMobile ? 56 : 70) }}>
                {/* Cột + đường ngang full width */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: chartPoints.length <= 3 ? 'flex-start' : 'space-between',
                    gap: { xs: 1, sm: 1.5 },
                    height: { xs: 150, sm: 180 },
                    width: '100%',
                    px: 0.5,
                    borderBottom: '2px solid',
                    borderColor: alpha(accentColor, 0.45),
                  }}
                >
                  {chartPoints.map((point) => (
                    <Box
                      key={point.id}
                      sx={{
                        flex: chartPoints.length <= 7 ? '1 1 0' : '0 0 auto',
                        minWidth: { xs: 44, sm: 56 },
                        maxWidth: 80,
                        height: '100%',
                        display: 'flex',
                        alignItems: 'flex-end',
                        justifyContent: 'center',
                        gap: 0.4,
                      }}
                    >
                      {series.map((s) => {
                        const value = point[s.key];
                        const pct = Math.max(value > 0 ? 6 : 0, (value / chartMax) * 100);
                        return (
                          <Box
                            key={s.key}
                            title={`${point.label} · ${s.label}: ${value}`}
                            sx={{
                              width: { xs: 10, sm: 14 },
                              height: `${pct}%`,
                              minHeight: value > 0 ? 4 : 0,
                              borderRadius: '4px 4px 0 0',
                              bgcolor: s.color,
                              transition: 'height 0.35s ease',
                            }}
                          />
                        );
                      })}
                    </Box>
                  ))}
                </Box>

                {/* Nhãn ngày dưới đường */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: chartPoints.length <= 3 ? 'flex-start' : 'space-between',
                    gap: { xs: 1, sm: 1.5 },
                    width: '100%',
                    px: 0.5,
                    mt: 0.75,
                  }}
                >
                  {chartPoints.map((point) => (
                    <Typography
                      key={`lbl-${point.id}`}
                      variant="caption"
                      sx={{
                        flex: chartPoints.length <= 7 ? '1 1 0' : '0 0 auto',
                        minWidth: { xs: 44, sm: 56 },
                        maxWidth: 80,
                        textAlign: 'center',
                        fontSize: { xs: '0.62rem', sm: '0.7rem' },
                        color: 'text.secondary',
                        lineHeight: 1.2,
                      }}
                    >
                      {point.label}
                    </Typography>
                  ))}
                </Box>
              </Box>
            </Box>
          </Paper>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {filteredItems.length} gallery
            {filteredItems.length > LIST_PAGE_SIZE
              ? ` · đang hiện ${Math.min(listVisibleCount, filteredItems.length)}`
              : ''}
            {!isAll ? ` · lọc ${selectedSizes.join(', ')}` : ''}
            {machineCodeFilter ? ` · máy ${machineCodeFilter}` : ''}
            {` · ${TIME_RANGES.find((t) => t.key === timeRange)?.label || ''}`}
          </Typography>

          {!filteredItems.length ? (
            <Paper
              elevation={0}
              sx={{
                p: 3,
                textAlign: 'center',
                border: '1px solid',
                borderColor: alpha(accentColor, 0.25),
                borderRadius: 2.5,
                bgcolor: '#fff',
              }}
            >
              <Typography color="text.secondary">
                Chưa có gallery được truy cập trong khoảng thời gian này.
              </Typography>
            </Paper>
          ) : (
            <Stack spacing={1.25}>
              {visibleListItems.map((g) => (
                <Accordion
                  key={g.folderId}
                  disableGutters
                  elevation={0}
                  sx={{
                    border: '1px solid',
                    borderColor: alpha(accentColor, 0.22),
                    borderRadius: '14px !important',
                    bgcolor: '#fff',
                    '&:before': { display: 'none' },
                    overflow: 'hidden',
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: accentColor }} />}
                    sx={{
                      px: { xs: 1.25, sm: 1.75 },
                      py: { xs: 1, sm: 1.25 },
                      alignItems: 'stretch',
                      '& .MuiAccordionSummary-content': { my: 0, width: '100%' },
                    }}
                  >
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={{ xs: 1.25, sm: 2 }}
                      alignItems={{ xs: 'stretch', sm: 'center' }}
                      sx={{ width: '100%', pr: { xs: 0, sm: 1 } }}
                    >
                      <Box
                        sx={{
                          flexShrink: 0,
                          width: { xs: '100%', sm: 120 },
                          height: { xs: 160, sm: 132 },
                          borderRadius: 2,
                          overflow: 'hidden',
                          bgcolor: alpha(accentColor, 0.06),
                          border: '1px solid',
                          borderColor: alpha(accentColor, 0.2),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          p: 1,
                          boxSizing: 'border-box',
                        }}
                      >
                        {g.coverUrl ? (
                          <Box
                            component="img"
                            src={g.coverUrl}
                            alt={g.folderId}
                            sx={{
                              maxWidth: '100%',
                              maxHeight: '100%',
                              width: 'auto',
                              height: 'auto',
                              objectFit: 'contain',
                              display: 'block',
                            }}
                          />
                        ) : (
                          <ImageIcon sx={{ fontSize: 40, color: alpha(accentColor, 0.45) }} />
                        )}
                      </Box>

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          fontWeight={800}
                          sx={{ fontSize: { xs: '0.95rem', sm: '1rem' }, color: '#0f3460' }}
                          noWrap={!isMobile}
                        >
                          {g.folderId}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mt: 0.35 }}>
                          <Typography variant="caption" color="text.secondary">
                            {g.createdAt ? format(new Date(g.createdAt), 'dd/MM/yyyy HH:mm') : '—'}
                          </Typography>
                          {g.machineCode && (
                            <Chip
                              label={g.machineCode}
                              size="small"
                              sx={{
                                height: 22,
                                fontWeight: 700,
                                fontSize: '0.7rem',
                                bgcolor: alpha(accentColor, 0.08),
                                color: 'text.secondary',
                              }}
                            />
                          )}
                          {g.printSize && (
                            <Chip
                              label={g.printSize}
                              size="small"
                              sx={{
                                height: 22,
                                fontWeight: 700,
                                fontSize: '0.7rem',
                                bgcolor: alpha(accentColor, 0.12),
                                color: accentColor,
                              }}
                            />
                          )}
                        </Stack>
                        <Stack direction="row" spacing={1.5} flexWrap="wrap" sx={{ mt: 0.85 }}>
                          <Typography variant="caption" fontWeight={800} sx={{ color: series[0].color }}>
                            {g.visitCount} lượt truy cập
                          </Typography>
                          <Typography variant="caption" fontWeight={700} sx={{ color: series[1].color }}>
                            {g.imageDownloadCount} ảnh đã tải
                          </Typography>
                          <Typography variant="caption" fontWeight={700} sx={{ color: series[2].color }}>
                            {g.videoDownloadCount} video đã tải
                          </Typography>
                          <Typography variant="caption" fontWeight={700} sx={{ color: series[3].color }}>
                            {g.printCount || 0} số lượt in
                          </Typography>
                        </Stack>
                      </Box>

                      {g.viewUrl && (
                        <IconButton
                          size="small"
                          component={Link}
                          href={g.viewUrl}
                          target="_blank"
                          rel="noopener"
                          onClick={(e) => e.stopPropagation()}
                          sx={{ alignSelf: { xs: 'flex-end', sm: 'center' }, color: accentColor }}
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0, px: { xs: 1.25, sm: 1.75 }, pb: 1.5 }}>
                    {(g.downloads || []).length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        Chưa có lượt tải ảnh/video.
                      </Typography>
                    ) : (
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>File</TableCell>
                            <TableCell>Loại</TableCell>
                            <TableCell align="right">Lượt tải</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {g.downloads.map((d) => (
                            <TableRow key={`${g.folderId}-${d.name}`}>
                              <TableCell sx={{ maxWidth: 220, wordBreak: 'break-all' }}>{d.name}</TableCell>
                              <TableCell>{d.mediaType === 'video' ? 'Video' : 'Ảnh'}</TableCell>
                              <TableCell align="right">{d.count}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </AccordionDetails>
                </Accordion>
              ))}

              {listHasMore && (
                <Box
                  ref={listSentinelRef}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  py={1.5}
                >
                  <CircularProgress size={22} sx={{ color: accentColor }} />
                </Box>
              )}
            </Stack>
          )}
        </>
      )}
    </Box>
  );
};

export default GalleryStatsPanel;
